import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { CalculationOutput } from "components/ui/display/CalculationOutput"
import { Input } from "components/ui/inputs/Input"
import { calculationLimits } from "store/calculation.config"
import styled, { useTheme } from "styled-components"
import { useErrors } from "providers/ErrorsProvider"
import { useEffect, useMemo, useState } from "react"
import { calculateOvertaking } from "./Overtaking.calculate"
import { parseVerticalGeometry } from "utilities/geometricRoadutilities/parseVerticalGeometry"
import { Table } from "components/ui/display/Table"
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts"
import { generateTicks } from "utilities/generateTicks"
import { FileDropzone } from "components/ui/inputs/FileDropzone"
import { MaterialIcon } from "components/ui/shared/MaterialIcon"
import { usePasteListener } from "utilities/pasteListener"

export const Overtaking = () => {
    const GlobalTheme = useTheme();
    const {setError, unsetError} = useErrors();

    const uploadFunction = async (acceptedFiles) => {
        const file = acceptedFiles[0];
        if(!file) return;

        const content = await file.text();

        const parsed = parseVerticalGeometry(content);
        if(!parsed)
        {
            setError('TOOLS_VERTICAL_GEOMETRY_DATA_INVALID');
            setCalculationInput(prev => ({...prev, verticalGeometry: null}));
        }
        else
        {
            unsetError('TOOLS_VERTICAL_GEOMETRY_DATA_INVALID');
            setCalculationInput(prev => ({...prev, verticalGeometry: parsed}));
        }
    }

    const pasteFunction = (e) => {
        const content = e.clipboardData?.getData('text');
        const parsed = parseVerticalGeometry(content);

        if(!parsed)
        {
            setError('TOOLS_VERTICAL_GEOMETRY_DATA_INVALID');
            setCalculationInput(prev => ({...prev, verticalGeometry: null}));
        }
        else
        {
            unsetError('TOOLS_VERTICAL_GEOMETRY_DATA_INVALID');
            setCalculationInput(prev => ({...prev, verticalGeometry: parsed}));
        }
    }

    usePasteListener(pasteFunction);
    
    const [rawInput, setRawInput] = useState({
        speedlimit: '',
        initialSpeed: '',
        aadt: '',
        aadtHeavy: ''
    });

    const [calculationInput, setCalculationInput] = useState({
        speedlimit: '',
        initialSpeed: '',
        aadt: '',
        aadtHeavy: '',
        verticalGeometry: null
    });

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateOvertaking(calculationInput);
        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }

        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });

    }, [calculationInput, setError, unsetError]);

    const XAxisTicks = useMemo(() => {
        if(!calculatedState.output?.minStationing?.valid || !calculatedState.output?.maxStationing?.valid) return null;

        return generateTicks({
            min: Math.round(calculatedState.output.minStationing.value),
            max: Math.round(calculatedState.output.maxStationing.value),
            targetTicks: 10
        });
    }, [calculatedState]);

    const Y1AxisTicks = useMemo(() => {
        if(!calculatedState.output?.minElevation?.valid || !calculatedState.output?.maxElevation?.valid) return null;

        return generateTicks({
            min: calculatedState.output.minElevation.value,
            max: calculatedState.output.maxElevation.value,
            targetTicks: 10,
            expand: true
        });
    }, [calculatedState]);

    const Y2AxisTicks = useMemo(() => {
        if(!calculatedState.output?.minSpeed?.valid || !calculatedState.output?.maxSpeed?.valid) return null;

        return generateTicks({
            min: calculatedState.output.minSpeed.value,
            max: calculatedState.output.maxSpeed.value,
            step: 10,
            expand: true
        });
    }, [calculatedState]);

    return (
        <StyledOvertaking>
            <ToolsColumn className="inputColumn">
                <ToolsContainer title="Inndata">
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error' ?? null}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning' ?? null}
                        value={rawInput.speedlimit}
                        onChange={(e) => setRawInput(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, speedlimit: e.target.value}))}
                    />
                    <Input
                        title="Startfart"
                        prefix={<span>v<sub>0</sub></span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.initialSpeed?.severity === 'error' ?? null}
                        warning={calculatedState.input?.initialSpeed?.severity === 'warning' ?? null}
                        value={rawInput.initialSpeed}
                        onChange={(e) => setRawInput(prev => ({...prev, initialSpeed: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, initialSpeed: e.target.value}))}
                    />
                    <Input
                        title="Årsdøgntrafikk"
                        prefix={<span>ådt</span>}
                        sufix='kjt/d.'
                        type='number'
                        error={calculatedState.input?.aadt?.severity === 'error' ?? null}
                        warning={calculatedState.input?.aadt?.severity === 'warning' ?? null}
                        value={rawInput.aadt}
                        onChange={(e) => setRawInput(prev => ({...prev, aadt: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, aadt: e.target.value}))}
                    />
                    <Input
                        title="Andel tungtrafikk"
                        prefix={<span>%tt</span>}
                        sufix='%'
                        type='number'
                        error={calculatedState.input?.aadtHeavy?.severity === 'er<ror' ?? null}
                        warning={calculatedState.input?.aadtHeavy?.severity === 'warning' ?? null}
                        value={rawInput.aadtHeavy}
                        onChange={(e) => setRawInput(prev => ({...prev, aadtHeavy: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, aadtHeavy: e.target.value}))}
                    />
                    <FileDropzone
                        title='Vertikalgeometri'
                        description='Slipp her eller velg gyldig vertikalgeometri fra datamaskinen.'
                        icon={<MaterialIcon name='ssid_chart' weight={300}/>}
                        helptext={'Alternativt lim inn data rett fra utkilpstavlen med CTRL + V / CMD + V.'}
                        acceptedFiles={['.tit', '.txt']}
                        maxFiles={1}
                        onUpload={uploadFunction}
                        acceptedExtensions={['nyl', 'txt']}
                    />
                </ToolsContainer>
                <ToolsContainer title="Faste variabler">
                    <Input
                        title="Fart - passiv kjøretøy"
                        prefix={<span>v<sub>pas</sub></span>}
                        sufix='km/t'
                        type='number'
                        value={calculatedState.derived?.speedPassive?.value?.toFixed(0) ?? ''}
                        disabled
                    />
                    <Input
                        title="Fart - aktiv kjøretøy"
                        prefix={<span>v<sub>act</sub></span>}
                        sufix='km/t'
                        type='number'
                        value={calculatedState.derived?.speedActive?.value?.toFixed(0) ?? ''}
                        disabled
                    />
                    <Input
                        title="Fart - motgående kjøretøy"
                        prefix={<span>v<sub>mot</sub></span>}
                        sufix='km/t'
                        type='number'
                        value={calculatedState.derived?.speedOncoming?.value?.toFixed(0) ?? ''}
                        disabled
                    />
                    <Input
                        title="Kritisk fart - start"
                        prefix={<span>v<sub>k,1</sub></span>}
                        sufix='km/t'
                        type='number'
                        value={calculatedState.derived?.criticalSpeedStart?.value?.toFixed(0) ?? ''}
                        disabled
                    />
                    <Input
                        title="Kritisk fart - slutt"
                        prefix={<span>v<sub>k,2</sub></span>}
                        sufix='km/t'
                        type='number'
                        value={calculatedState.derived?.criticalSpeedEnd?.value?.toFixed(0) ?? ''}
                        disabled
                    />
                </ToolsContainer>
            </ToolsColumn>

            <ToolsColumn className="outputColumn">
                <CalculationOutput
                    title='Forbikjøringssikt'
                    value={calculatedState.output?.overtakingLength?.value?.toFixed(0) ?? "--"}
                    prefix={<span>L<sub>f</sub></span>}
                    sufix='m'
                />
                {
                    calculatedState.output?.overtakingIntervalsForward?.valid ?
                    calculatedState.output.overtakingIntervalsForward.value.filter(el => el.overtakingLane === true).map((el, i) => 
                        <CalculationOutput
                            key={i}
                            title='Forbikjøringsstrekning fremover'
                            value={<span>{el.data[0].stationing.toFixed(3)} - {el.data[el.data.length-1].stationing.toFixed(3)}</span>}
                        />
                    )
                    :null
                }
                {
                    calculatedState.output?.overtakingIntervalsBackward?.valid ?
                    calculatedState.output.overtakingIntervalsBackward.value.filter(el => el.overtakingLane === true).map((el, i) => 
                        <CalculationOutput
                            key={i}
                            title='Forbikjøringsstrekning bakover'
                            value={<span>{el.data[el.data.length-1].stationing.toFixed(3)} - {el.data[0].stationing.toFixed(3)}</span>}
                        />
                    )
                    :null
                }
            </ToolsColumn>

            <ToolsColumn className="infoColumn">
                {
                    calculatedState.input?.verticalGeometry?.valid ? 
                    <ToolsContainer title='Importert vertikalgeometri'>
                        <Table
                            headers={[
                                {key: 'name', label: <span>Element</span>},
                                {key: 'startStationing', label: <span>Startprofil</span>, unit: '[m]'},
                                {key: 'radius', label: <span>Radius</span>, unit: '[m]'},
                                {key: 'slope', label: <span>Stigning</span>, unit: '[m/m]'},
                                {key: 'length', label: <span>Lengde</span>, unit: '[m]'},
                            ]}
                            data={calculatedState.input.verticalGeometry.value.map(tr => {
                                if(tr.type === 'line') tr.name = 'Rettlinje';
                                else if(tr.type === 'arc') tr.name = 'Sirkelbue';
                                return tr;
                            })}
                        />
                    </ToolsContainer>
                    : null
                }
                {
                    calculatedState.input?.verticalGeometry?.valid && calculatedState.input?.verticalPoints?.valid ? 
                    <ToolsContainer title="Vertikalprofil">
                        <div className="graphContainer">
                            <ResponsiveContainer
                                height='100%'
                                width='100%'
                                minWidth='400px'
                                minHeight='0'
                                aspect={undefined}
                            >
                                <LineChart
                                    margin={{top: 10, left: 30, right: 30, bottom: 0}}
                                >
                                    <Legend verticalAlign="bottom" layout="vertical"/>
                                    <XAxis
                                        type='number'
                                        height={50}
                                        dataKey='stationing'
                                        //xAxisId={'stationing'}
                                        fontSize={12}
                                        ticks={XAxisTicks}
                                        domain={['dataMin', 'dataMax']}
                                        label={{
                                            value: 'Profil [m]',
                                            position: 'center',
                                            dy: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                    <YAxis
                                        yAxisId={'elevation'}
                                        orientation={'left'}
                                        fontSize={12}
                                        ticks={Y1AxisTicks}
                                        label={{
                                            value: 'Høyde [m.o.h.]',
                                            angle: -90,
                                            position: 'left',
                                            dx: 10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                    />
                                    <YAxis
                                        yAxisId={'speed'}
                                        orientation={'right'}
                                        ticks={Y2AxisTicks}
                                        label={{
                                            value: 'Hastighet [km/t]',
                                            angle: 90,
                                            position: 'right',
                                            dx: -10,
                                            style: {
                                                textAnchor: 'middle',
                                            },
                                        }}
                                        fontSize={12}
                                    />
                                    <CartesianGrid
                                        strokeDasharray={'3 3'}
                                        yAxisId={'speed'}
                                    />
                                    <Line
                                        data={calculatedState.input?.verticalPoints?.value}
                                        name='Høyde'
                                        dataKey={'elevation'}
                                        yAxisId={'elevation'}
                                        dot={false}
                                        strokeWidth={2}
                                        stroke={GlobalTheme.colors.neutralPrimaryForeground}
                                        activeDot={false}
                                    />
                                    {
                                        calculatedState.output.overtakingIntervalsForward.valid ?
                                        calculatedState.output.overtakingIntervalsForward.value.map((arr, i) => <Line
                                            key={i}
                                            data={arr.data}
                                            name='Forbikjøringsinterval fremover'
                                            dataKey={'speed'}
                                            yAxisId={'speed'}
                                            dot={false}
                                            strokeWidth={2}
                                            stroke={GlobalTheme.colors.successForeground}
                                            strokeDasharray={arr.overtakingLane ? null : '3 3'}
                                            legendType={i === 0 ? 'line' : 'none'}
                                            activeDot={false}
                                        />)
                                        : null
                                    }
                                    {
                                        calculatedState.output.overtakingIntervalsBackward.valid ?
                                        calculatedState.output.overtakingIntervalsBackward.value.map((arr, i) => <Line
                                            key={i}
                                            data={arr.data}
                                            name='Forbikjøringsinterval bakover'
                                            dataKey={'speed'}
                                            yAxisId={'speed'}
                                            dot={false}
                                            strokeWidth={2}
                                            stroke={GlobalTheme.colors.warningForeground}
                                            strokeDasharray={arr.overtakingLane ? null : '3 3'}
                                            legendType={i === 0 ? 'line' : 'none'}
                                            activeDot={false}
                                        />)
                                        : null
                                    }
                                    
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ToolsContainer>
                    : null
                }
                
            </ToolsColumn>
        </StyledOvertaking>
    )
}

const StyledOvertaking = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }

    div.graphContainer
    {
        min-height: 300px;
        overflow: auto;
    }
`

